/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect } from 'react'
import {
  Button,
  TextField,
  Stack,
  Typography,
  Box,
  Modal,
  Grid,
  FormControl,
  FormHelperText,
  FormControlLabel,
  Select as MuiSelect,
  MenuItem,
  CircularProgress,
} from '@mui/material'
import { useFormik } from 'formik'
import * as yup from 'yup'
import { toast } from 'react-toastify'
import { IDealerGroups, IUpdateDealerGroupRequest, ICreatedealerGroupRequest } from '../../types'
import {
  useCreateDealerGroupMutation,
  useUpdateDealerGroupMutation,
} from '../../redux/api/dealerGroupApi'

const styles = {
  container: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 400,
    bgcolor: 'background.paper',
    boxShadow: 24,
    borderRadius: '12px',
  },
  headerStyle: {
    borderRadius: '12px',
    display: 'flex',
    p: 2,
    justifyContent: 'center',
    backgroundColor: '#1976d2',
    mb: 4,
  },
}

interface IDealerGroup {
  dealerGroupName: string
  dealerGroupNumber: string
}

interface Props {
  editableDealerGroup?: IDealerGroups
  onClose: () => void
  refetchDealerGroups: () => void
  open: boolean
  dealerGroupNumbersList: IDealerGroup[]
  setDealerList: React.Dispatch<React.SetStateAction<IDealerGroup[]>>
  dealerList: IDealerGroup[]
  dealerTypes: any[]
  resetForm: string
}

const validationSchema = yup.object({
  dealerGroupName: yup.string().required('Dealer Group name is required'),
  dealerGroupNumber: yup.string().required('Dealer Group Number is required'),
  dealerType: yup.string().required('Dealer Type is required'),
  logoImg: yup.mixed().notRequired(),
})

export const DealerGroupForm = ({
  onClose,
  refetchDealerGroups,
  open,
  editableDealerGroup,
  dealerGroupNumbersList,
  dealerList,
  setDealerList,
  resetForm,
  dealerTypes,
}: Props) => {
  const [createDealerGroup, { isLoading, isError, error, isSuccess }] =
    useCreateDealerGroupMutation()
  const [
    updateDealerGroups,
    { isLoading: uIsLoading, isError: uIsError, error: uError, isSuccess: uIsSuccess },
  ] = useUpdateDealerGroupMutation()
  const [dealerGroupRemoved, setDealerGroupRemoved] = useState<IDealerGroup>()
  const [logoImg, setLogoImg] = React.useState<File | null>(null)
  const [logoImgUrl, setLogoImgUrl] = React.useState<any>(null)
  const [dealerIds, setDealerIds] = useState<string[]>([])

  const formik = useFormik({
    initialValues: {
      application: editableDealerGroup?.application ?? 'Intranet',
      dealerGroupName: editableDealerGroup?.dealerGroup ?? '',
      dealerGroupNumber: editableDealerGroup?.dealerGroupNumber ?? '',
      dealerLogoPath: editableDealerGroup?.dealerLogoPath ?? '',
      dealerType: editableDealerGroup?.dealerTypeId?.toString() ?? '',
      dealerId: [],
    },
    validationSchema,
    onSubmit: (values) => {
      if (!values.dealerType || isNaN(parseInt(values.dealerType, 10))) {
        toast.error('Please select a valid dealer type', { position: 'top-right' })
        return
      }

      const validDealerIds = dealerIds.map((id) => id.trim()).filter((id) => id !== '')
      const requestData = {
        application: values.application ?? 'Intranet',
        dealerGroup: values.dealerGroupName || null,
        dealerGroupNumber: values.dealerGroupNumber || null,
        dealerLogoPath: values.dealerLogoPath || null,
        dealerType: parseInt(values.dealerType, 10),
        dealerId: validDealerIds,
        logoImg: logoImg,
      }

      if (editableDealerGroup?.dealerGroupId) {
        const updateData: any = {
          ...requestData,
          dealerGroupId: editableDealerGroup.dealerGroupId,
        }
        updateDealerGroups(updateData)
          .unwrap()
          .then(() => {
            refetchDealerGroups()
            if (dealerGroupRemoved) {
              const filteredArray = dealerGroupNumbersList.filter(
                (item1) => item1.dealerGroupNumber !== dealerGroupRemoved.dealerGroupNumber,
              )
              setDealerList(filteredArray)
            }
            toast.success('Dealer group updated successfully', { position: 'top-right' })
          })
          .catch((err) => {
            toast.error(err?.data?.message ?? 'Failed to update dealer group', {
              position: 'top-right',
            })
          })
      } else {
        const createData: any = {
          ...requestData,
        }
        createDealerGroup(createData)
          .unwrap()
          .then(() => {
            refetchDealerGroups()
            if (dealerGroupRemoved) {
              const filteredArray = dealerGroupNumbersList.filter(
                (item1) => item1.dealerGroupNumber !== dealerGroupRemoved.dealerGroupNumber,
              )
              setDealerList(filteredArray)
            }
            toast.success('Dealer group created successfully', { position: 'top-right' })
            setDealerIds([])
            formik.setFieldValue('dealerId', [])
          })
          .catch((err) => {
            toast.error(err?.data?.message ?? 'Failed to create dealer group', {
              position: 'top-right',
            })
          })
      }
    },
  })

  React.useEffect(() => {
    if (isSuccess) {
      toast.success('Success!')
      formik.resetForm()
      onClose()
      refetchDealerGroups()
    }
    if (isError) {
      if (Array.isArray((error as any)?.data?.error)) {
        const errors = error as any
        errors?.data.error.forEach((el: any) =>
          toast.error(el?.message ?? 'Something Failed!', {
            position: 'top-right',
          }),
        )
      } else {
        toast.error((error as any)?.data?.message ?? 'Something Failed!', {
          position: 'top-right',
        })
      }
    }
  }, [isLoading])

  React.useEffect(() => {
    if (uIsSuccess) {
      toast.success('Success!')
      formik.resetForm()
      onClose()
      refetchDealerGroups()
    }
    if (uIsError) {
      if (Array.isArray((uError as any)?.data?.error)) {
        const errors = uError as any
        errors?.data.error.forEach((el: any) =>
          toast.error(el?.message ?? 'Something Failed!', {
            position: 'top-right',
          }),
        )
      } else {
        toast.error((uError as any)?.data?.message ?? 'Something Failed!', {
          position: 'top-right',
        })
      }
    }
  }, [uIsLoading])

  React.useEffect(() => {
    if (editableDealerGroup && editableDealerGroup.dealerId) {
      const dealerIdArray = (
        Array.isArray(editableDealerGroup.dealerId)
          ? editableDealerGroup.dealerId
          : [editableDealerGroup.dealerId]
      ).flatMap((id: string | null | undefined) =>
        id
          ? id
              .split(',')
              .map((item) => item.trim())
              .filter((item) => item !== '')
          : [],
      )
      setDealerIds(dealerIdArray.length > 0 ? dealerIdArray : [''])
      formik.setValues({
        application: editableDealerGroup?.application ?? 'Intranet',
        dealerGroupName: editableDealerGroup?.dealerGroup ?? '',
        dealerGroupNumber: editableDealerGroup?.dealerGroupNumber ?? '',
        dealerLogoPath: editableDealerGroup?.dealerLogoPath ?? '',
        dealerType: editableDealerGroup?.dealerTypeId?.toString() ?? '',
        dealerId: [],
      })
    } else if (editableDealerGroup) {
      setDealerIds([''])
      formik.setValues({
        application: editableDealerGroup?.application ?? 'Intranet',
        dealerGroupName: editableDealerGroup?.dealerGroup ?? '',
        dealerGroupNumber: editableDealerGroup?.dealerGroupNumber ?? '',
        dealerLogoPath: editableDealerGroup?.dealerLogoPath ?? '',
        dealerType: editableDealerGroup?.dealerTypeId?.toString() ?? '',
        dealerId: [],
      })
    }
  }, [editableDealerGroup])

  React.useEffect(() => {
    if (!open) {
      resetImg()
      if (!editableDealerGroup) {
        setDealerIds([])
        formik.setFieldValue('dealerId', [])
      }
    }
  }, [open])

  const resetImg = () => {
    setLogoImg(null)
    setLogoImgUrl(null)
  }

  const reset = () => {
    if (editableDealerGroup) {
      const filteredArray = dealerGroupNumbersList.filter(
        (item1) => item1.dealerGroupNumber !== editableDealerGroup.dealerGroupNumber,
      )
      setDealerList(filteredArray)
    }
    setLogoImg(null)
    setLogoImgUrl(null)
    setDealerIds([])
    formik.setValues({
      application: 'Intranet',
      dealerGroupName: '',
      dealerGroupNumber: '',
      dealerLogoPath: '',
      dealerType: '',
      dealerId: [],
    })
    formik.resetForm()
    onClose()
  }

  const handleAddDealerId = () => {
    const newDealerId: string = ((formik.values.dealerId[0] as string) || '')?.trim()

    if (!newDealerId) {
      return
    }

    const lowerNewId = newDealerId.toLowerCase()
    const alreadyExists = dealerIds.some((existing) => existing.trim().toLowerCase() === lowerNewId)

    if (alreadyExists) {
      toast.warn(`Dealer ID "${newDealerId}" is already added (case-insensitive)`, {
        position: 'top-right',
      })
      formik.setFieldValue('dealerId[0]', '')
      return
    }

    const updatedDealerIds = [...dealerIds.filter((id) => id.trim() !== ''), newDealerId]
    setDealerIds(updatedDealerIds)
    formik.setFieldValue('dealerId[0]', '')
    formik.setFieldValue('dealerId', [])
  }

  const handleRemoveDealerId = (index: number) => {
    const newDealerIds = dealerIds.filter((_, i) => i !== index)
    setDealerIds(newDealerIds.length > 0 ? newDealerIds : [''])
    formik.setFieldValue('dealerId', [])
  }

  const inputDealerIdValue = formik.values.dealerId[0] || (dealerIds.includes('') ? '' : '')

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby='modal-modal-title'
      aria-describedby='modal-modal-description'
    >
      <Box sx={styles.container}>
        <Box sx={styles.headerStyle}>
          <Typography id='modal-modal-title' variant='h5' component='h2' color='white'>
            Dealer Group Details
          </Typography>
        </Box>
        <Box sx={{ p: 2 }}>
          <form onSubmit={formik.handleSubmit}>
            <Stack spacing={2}>
              <TextField
                fullWidth
                id='dealerGroupName'
                name='dealerGroupName'
                label='Dealer Group'
                value={formik.values.dealerGroupName}
                onChange={formik.handleChange}
                error={formik.touched.dealerGroupName && Boolean(formik.errors.dealerGroupName)}
                helperText={formik.touched.dealerGroupName && formik.errors.dealerGroupName}
              />
              <FormControl
                fullWidth
                error={formik.touched.dealerType && Boolean(formik.errors.dealerType)}
              >
                <MuiSelect
                  id='dealerType'
                  name='dealerType'
                  label='Dealer Type'
                  value={formik.values.dealerType}
                  onChange={formik.handleChange}
                >
                  {dealerTypes.map((type: any) => (
                    <MenuItem key={type.dealerTypeId} value={type.dealerTypeId.toString()}>
                      {type.dealerType}
                    </MenuItem>
                  ))}
                </MuiSelect>
                {formik.touched.dealerType && formik.errors.dealerType && (
                  <FormHelperText>{formik.errors.dealerType}</FormHelperText>
                )}
              </FormControl>
              <TextField
                fullWidth
                id='dealerGroupNumber'
                name='dealerGroupNumber'
                label='Dealer Group Number'
                value={formik.values.dealerGroupNumber}
                onChange={formik.handleChange}
                error={formik.touched.dealerGroupNumber && Boolean(formik.errors.dealerGroupNumber)}
                helperText={formik.touched.dealerGroupNumber && formik.errors.dealerGroupNumber}
              />
              <Box display='flex' alignItems='center' gap={1}>
                <TextField
                  fullWidth
                  id='dealerId[0]'
                  name='dealerId[0]'
                  label='Dealer ID'
                  value={inputDealerIdValue}
                  onChange={(e) => {
                    formik.setFieldValue('dealerId[0]', e.target.value)
                  }}
                  // error={formik.touched.dealerId && Boolean(formik.errors.dealerId)}
                  // helperText={
                  //   formik.touched.dealerId &&
                  //   Array.isArray(formik.errors.dealerId) &&
                  //   formik.errors.dealerId.length > 0
                  //     ? formik.errors.dealerId[0]
                  //     : ''
                  // }
                />
                <Button variant='contained' color='primary' onClick={handleAddDealerId}>
                  + Add
                </Button>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  width: '100%',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  borderRadius: '20px',
                  padding: '5px',
                  maxHeight: '200px',
                  overflowY: 'auto',
                  overflowX: 'hidden',
                }}
              >
                {dealerIds
                  .filter((id) => id !== '' && id.trim() !== '')
                  .map((id, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: 'block',
                        borderRadius: '20px',
                        padding: '5px',
                      }}
                      gap={1}
                    >
                      <Button
                        variant='contained'
                        size='large'
                        sx={{
                          fontSize: '1rem',
                          textDecoration: 'none',
                          backgroundColor: '#1976d2',
                          borderRadius: '5px',
                          padding: '5px 10px',
                        }}
                        onClick={() => handleRemoveDealerId(index)}
                      >
                        {id} <span style={{ margin: '2px 10px' }}>X</span>
                      </Button>
                    </Box>
                  ))}
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <FormControlLabel
                    control={
                      <>
                        <TextField
                          id='logoImg'
                          name='logoImg'
                          type='file'
                          inputProps={{
                            multiple: false,
                          }}
                          sx={{ display: 'none' }}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
                            if (e.target.files && e.target.files[0]) {
                              setLogoImgUrl(URL.createObjectURL(e.target.files[0]))
                              setLogoImg(e.target.files[0])
                            }
                          }}
                        />
                        <Button color='primary' variant='contained' component='span'>
                          + Logo
                        </Button>
                      </>
                    }
                    htmlFor='logoImg'
                    label=''
                  />
                </Grid>
                <Grid item xs={6}>
                  <Box textAlign='center'>
                    <img
                      src={
                        logoImgUrl
                          ? logoImgUrl
                          : resetForm === 'update' && formik.values.dealerLogoPath
                          ? formik.values.dealerLogoPath
                          : ''
                      }
                      width='100px'
                    />
                  </Box>
                </Grid>
              </Grid>
            </Stack>
            <Box sx={{ textAlign: 'right', mt: '30px' }}>
              <Button
                variant='outlined'
                size='large'
                sx={{ mr: 2, borderRadius: '20px' }}
                color='error'
                onClick={reset}
                disabled={uIsLoading || isLoading}
              >
                {uIsLoading || isLoading ? (
                  <CircularProgress size={24} color='inherit' />
                ) : (
                  'Cancel'
                )}
              </Button>
              <Button
                variant='contained'
                sx={{ borderRadius: '20px' }}
                size='large'
                type='submit'
                disabled={uIsLoading || isLoading}
              >
                {uIsLoading || isLoading ? (
                  <CircularProgress color='primary' size={24} />
                ) : (
                  'Submit'
                )}
              </Button>
            </Box>
          </form>
        </Box>
      </Box>
    </Modal>
  )
}
